import _ from 'lodash';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';

import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {PatientInfoService} from '@ispm/user/services/patientInfo.service';
import {TablelistStoreHandlerService} from '../../../worklist/services/tablelist-store-handler.service';
import {WebConfigService} from '@ispm/layout/services/webConfig.service';
import {WorkflowService} from '@ispm/workflow/services/workflow.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
    selector: 'gen-path-home',
    templateUrl: './path-home.component.html',
    styleUrls: ['./path-home.component.scss'],
})

export class PathHomeComponent implements OnInit, OnDestroy {

    public worklistTitle = 'Intellispace Genomics Worklist';
    public user: any;
    public showFilters = true;
    public showCreatePatientButton = false;
    public displayMyWorklist: any;
    public loading: boolean;
    private analysisResults: any;
    public categories: any = [];
    private runningTestStatus = 'Running';
    private pendingTestsStatus = 'Pending Control';
    private dayInMilliseconds = (24 * 1000 * 3600);
    private searchText: any;
    private worklistProperty: any;
    public selectedCategoryName: any;
    public worklistSearchText: any;
    public searchLoading = true;
    private subscriber: any;

    _resAnalysis: any;
    @Input() set resAnalyses(resAnalysisData: any) {
      this._resAnalysis = resAnalysisData;
    }

    constructor(
        private authService: AuthenticationService,
        private patientInfoService: PatientInfoService,
        private routerManagerService: RouterManagerService,
        private workflowService: WorkflowService,
        private tablelistStoreHandlerService: TablelistStoreHandlerService,
        private webConfigService: WebConfigService,
    ) { }

    ngOnInit() {
        this.user = this.authService.user;
        this.displayMyWorklist = this.authService.getLocalSetting('displayMyWorklist', true);
        this.selectedCategoryName = this.authService.getLocalSetting('selectedCategoryName', 'All');
        this.worklistSearchText = this.authService.getLocalSetting('worklistSearchText', '');

        const filter = { MRN: this.routerManagerService.parameters.mrn, patientId: this.routerManagerService.parameters.patientId};
        if (filter.MRN || filter.patientId) {
              this.displayMyWorklist = false;
        }

        this.subscriber = this.webConfigService.getWebConfiguration().subscribe(webConfig => {
            if (webConfig.init) {
              let headerObj = null;
              const navigationItems = !!webConfig['navigation'] && !!webConfig['navigation']['items'] ? webConfig['navigation']['items'] : undefined;
              if (!!navigationItems && Array.isArray(navigationItems)) {
                headerObj = webConfig.navigation.items.find(navigationItem => {
                  return (navigationItem.identify === this.getCurrectStateName() && navigationItem.enable);
                });
              }
              this.showFilters = true;
              if (headerObj) {
                this.worklistTitle = headerObj.headerTitle;
                if (this.getCurrectStateName() === 'patientNav') {
                  const config = this.getShowButtonFromWebConfig(webConfig);
                  if (config && typeof config.showCreatePatientButton !== 'undefined') {
                    this.showCreatePatientButton = config.showCreatePatientButton;
                  } else {
                    this.showCreatePatientButton = true;
                  }
                  this.showFilters = false;
                }
              }
            }
        });

        this.setWorklist();
    }

    private getCurrectStateName(): any {
        let identifier = this.routerManagerService.currentState !== 'patients'
          ? 'testListNav'
          : 'patientNav';
        if (this.routerManagerService.currentState === 'curation') { // this in case we on curation
          identifier = 'curtionNav';
        }
        return identifier;
    }

    private getShowButtonFromWebConfig(webConf): any {
        return webConf.navigation.items.find(element =>  element['identify'] === 'patientNav');
    }

    public isUserAdmin(): boolean {
        return this.user ? this.user.isAdmin : false;
    }

    public createPatient() {
        this.patientInfoService.openPatientInfo(null).subscribe((resp) => {
          if (resp.status !== 2 && resp.data === 1) {
            setTimeout(() => {
              this.routerManagerService.reload();
            }, 2000);
          }
        });
    }

    public async getAnalyses(filter?) {
        this.loading = true;
        (this.workflowService.getPathWorkList(this.displayMyWorklist, filter, this.authService.getUser())).subscribe((res) => {
            this._resAnalysis = res;
            this.setWorklist();
            this.loading = false;
          },
          () => {
            this.displayMyWorklist = !this.displayMyWorklist;
            this.loading = false;
        });
    }

    private setWorklist() {
        if (!this._resAnalysis) { return; }

        this.updateLocalSettings('displayMyWorklist', this.displayMyWorklist);
        this.analysisResults = this._resAnalysis.body.workflows;
        this.categories = Array.of(this._resAnalysis.body.workflowCategories) || { };
        const testElapsedTime = this._resAnalysis.body.testElapsedTime;
        let currentDate = new Date().getTime(), diff;
        this.analysisResults = !this.analysisResults ? [] : this.analysisResults.map(workflow => {
            // calculate status for clinical tests by suitable QC test
            workflow.testTags = this.createTestTags(workflow);

            workflow.revDatetime = new Date(workflow.revDatetime);
            if (testElapsedTime || testElapsedTime === 0) {
              workflow.revDatetime = new Date(workflow.revDatetime);
              diff = Math.ceil(Math.abs(currentDate - workflow.revDatetime.getTime()) / this.dayInMilliseconds);
              workflow.isTestOverdue = (diff >= testElapsedTime);
            }
            return workflow;
          });
        this.filterWorklist();
    }

    private updateLocalSettings(propName, value) {
        this.authService.updateLocalSettings(propName, value);
    }

    private createTestTags(workflow): any {
        if (workflow.relatedQCOrdersData != null) {
          // 27.09.18 We have received back list of connected control samples in DESC order: first one is the latest, then will show its status only
          const haveRunningTests = workflow.relatedQCOrdersData[0].orderWorkflowRequestStatus === this.runningTestStatus;
          return haveRunningTests ? [this.pendingTestsStatus] : [workflow.relatedQCOrdersData[0].orderResultStatus];
        }
        return [null];
    }

    private filterWorklist() {
        this.tablelistStoreHandlerService.getTablelistTemplateSnapshot('test').then((data) => {
          if (data.title) {
            this.worklistTitle = data.title;
          }
        });
        this.loading = true;

        if (!(this.analysisResults && this.worklistProperty)) { return; }

        const categories: any = [];
        categories.All = { name: 'All', count: 0};
        this.worklistProperty = this.worklistProperty || [];
        const categoryName = this.authService.getLocalSetting('selectedCategoryName', 'All');
        this.searchText = this.authService.getLocalSetting('worklistSearchText', '');
        this.analysisResults = this.analysisResults || [];
        let categoryFilter, searchFilter, revStatus;
        const searchText = this.searchText.toLowerCase();

        this.analysisResults.filter((analysis) => {
          categoryFilter = analysis.revStatus.toLowerCase() === categoryName.toLowerCase() || 'All' === categoryName;
          // todo:remove all of the logic of free text search since it's already exists inside worklist component.
          if (!this.searchText) {
            searchFilter = true;
          }
          else {
            searchFilter = this.worklistProperty.find((prop) => {
              const originalValue = this.getPropertyValueByPath(analysis, prop);
              if (originalValue) {
                return (originalValue as any).some(el => {
                  if (el instanceof Date) {
                    el = (el as any).toPlatformFormat();
                  }
                  return el.toLowerCase().includes(searchText);
                });
              }
              else {
                return false;
              }
            });
          }
          if (searchFilter && searchFilter !== '') {
            revStatus = analysis.revStatus.toLowerCase();
            if (!categories.hasOwnProperty(revStatus)) {
              categories[revStatus] = { name: analysis.revStatus, count: 0};
            }

            categories[revStatus].count++;
            categories.All.count++;
          }
          return categoryFilter && searchFilter;
        });
        this.setCategories(categories);
        this.loading = false;
    }

    private getPropertyValueByPath(obj, propName) {
        try {
          obj = Object.assign({ }, obj);
          let current = _.get(obj, propName, '');
          if (current instanceof Date) {
            current = (current as any).toPlatformFormat();
          }
          else if (Array.isArray(current)) {
            current = current.map(curr => {
              if (typeof curr === 'object') {
                return curr.display || curr.value;
              } else {
                return curr;
              }
            });
          }
          else {
            current = current.toString();
 }
          return Array.isArray(current) ? current : [current];
        } catch (e) {
          return '';
        }
    }

    private setCategories(categories) {
        if (Object.keys(categories).length > 0 && !categories.hasOwnProperty(this.selectedCategoryName)) {
          return this.filterByCategory('All');
        }
        const categoriesArray = [];
        for (const category in categories) {
          if (category.toLowerCase() === 'all') { continue; }
          categoriesArray.push({
            name: categories[category].name,
            count: categories[category].count,
            categoryProperty: category
          });
        }
        categoriesArray.sort((current, next) => current.name - next.name);
        categoriesArray.push({ name: 'All', count: categories.All.count, categoryProperty: 'All', });
        this.categories = categoriesArray;
    }

    public filterByCategory(categoryName) {
        this.selectedCategoryName = categoryName;
        this.updateLocalSettings('selectedCategoryName', categoryName);
        this.filterWorklist();
    }

    public trackCategory(index: number, category) {
        return category.name;
    }

    public filterByFreeText($event) {
        this.worklistSearchText = $event;
        if (this.worklistSearchText === '') {
          this.searchLoading = false;
        }
        this.updateLocalSettings('worklistSearchText', this.worklistSearchText);
        this.filterWorklist();
    }

    public updateLoadingStatus(status) {
        this.searchLoading = status;
    }

    public getId(category): string {

      return 'cat-' + category?.name?.replace(' ', '-');
    }

    ngOnDestroy() {
        if (this.subscriber) {
            this.subscriber.unsubscribe();
        }
    }
}
